import React from 'react';
import { SelectBox, Option } from 'src/components/SelectBox';
import { Label } from 'src/components/Label';
import { Check, LockOpen } from '@phosphor-icons/react';
import { Button } from 'src/components/Button';
import { useIntegrationApp, useConnections } from '@integration-app/react';
import { LockKeyOpen } from '@phosphor-icons/react';

export interface ProviderSelectProps {
  providers: Option[];
  selectedProvider: Option;
  onChange: (providerOption: Option) => void;
  isAuthenticated?: boolean;
  label: string;
  htmlFor: string;
  className?: string;
  key?: string;
  compact?: boolean;
}

export const ProviderSelect: React.FC<ProviderSelectProps> = ({
  providers,
  selectedProvider,
  onChange,
  isAuthenticated = false,
  label,
  htmlFor,
  className = 'w-full bg-neutral-0',
  key,
  compact = false,
}) => {
  const integrationApp = useIntegrationApp();
  const {
    connections: integrationAppConnections,
    refresh: refreshIntegrationAppConnections,
  } = useConnections();

  const handleAuthenticateProvider = () => {
    const connectionKey = selectedProvider.value;
    integrationApp?.integration(connectionKey).open({
      onClose: () => {
        refreshIntegrationAppConnections();
      },
    });
  };

  const isProviderAuthenticated = React.useMemo(() => {
    if (!selectedProvider.value) return false;

    const connection = integrationAppConnections.find(
      (conn) =>
        conn.integration?.key === selectedProvider.value && !conn.disconnected,
    );

    return !!connection;
  }, [integrationAppConnections, selectedProvider.value]);

  return (
    <div
      key={key}
      className={`w-full flex-shrink-0 ${compact ? 'mb-0' : 'mb-4'}`}
    >
      <Label htmlFor={htmlFor}>{label}</Label>
      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-y-2 sm:gap-x-8">
        <div className="w-full flex-shrink-0">
          <SelectBox
            options={providers}
            value={selectedProvider?.value ? selectedProvider : null}
            variant="outlined"
            color="primary"
            className={className}
            onChange={(value) => {
              const selectedProviderOption = providers.find(
                (provider) => provider.value === value,
              );

              const providerOption = {
                value,
                label: selectedProviderOption?.label || value,
              };

              onChange(providerOption);
            }}
            allowRemoveOption={true}
          />
        </div>
        {selectedProvider?.value && (
          <div className="w-full sm:w-80 flex-shrink-0">
            <Button
              type="button"
              variant="text"
              className="text-primary-600 text-md flex items-center justify-center gap-1 hover:text-primary-700"
              onClick={handleAuthenticateProvider}
            >
              <LockKeyOpen className="mr-1 h-4 w-4" />
              {isProviderAuthenticated ? 'Re-authenticate' : 'Authenticate'}
            </Button>
          </div>
        )}
      </div>
      {selectedProvider?.value && (
        <div
          className={`flex items-center max-w-md ${compact ? 'mt-2 mb-0' : 'mt-4 mb-6'}`}
        >
          {isAuthenticated || isProviderAuthenticated ? (
            <>
              <Check className="h-4 w-4 mr-2 text-success flex-shrink-0" />
              <span className="text-sm text-success">
                Connected to {selectedProvider.label || selectedProvider.value}
              </span>
            </>
          ) : (
            <>
              <LockOpen className="h-4 w-4 mr-2 text-warning flex-shrink-0" />
              <span className="text-sm text-warning">
                Authentication required for{' '}
                {selectedProvider.label || selectedProvider.value}
              </span>
            </>
          )}
        </div>
      )}
    </div>
  );
};
